import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { PlusIcon, UserCanceledIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'

import {
  assignEnrollmentsToCenterGroup,
  unassignEnrollmentFromCenterGroup
} from '../../../api/centers/center-groups'
import { EnrollmentCenter } from '../../../lib/types/models/enrollment-center/enrollment-center'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import AssignCenterGroupForm from '../centers/center-groups/assign-center-group-form'
import CenterGroupCircle from '../centers/center-groups/center-group-circle'

type Permission = 'ASSIGN_CENTER_GROUP' | 'UNASSIGN_CENTER_GROUP'
type Props = {
  permissions: Permission[]
  enrollmentCenter: EnrollmentCenter
  hideUnassignLabel?: boolean
  onSuccess?: () => void
}

const AssignCenterGroupCell: React.FC<Props> = ({
  permissions,
  enrollmentCenter,
  hideUnassignLabel = true,
  onSuccess
}) => {
  // The group is read from the row so the cell survives table re-renders; callers refetch the
  // table through `onSuccess` after an assignment so the row carries the new group.
  const centerGroup = enrollmentCenter.centerGroup
  const [open, setOpen] = useState(false)
  const { mutateAsync: assignEnrollmentsMutation, isPending: isAssignEnrollmentsPending } =
    useMutation({
      mutationFn: assignEnrollmentsToCenterGroup,
      onSuccess: () => {
        setOpen(false)
        onSuccess?.()
      },
      onError: showAxiosError
    })

  const {
    mutateAsync: unassignEnrollmentFromCenterGroupMutation,
    isPending: isUnassignEnrollmentPending
  } = useMutation({
    mutationFn: unassignEnrollmentFromCenterGroup,
    onSuccess: () => {
      onSuccess?.()
      setOpen(false)
    },
    onError: showAxiosError
  })

  return (
    <div>
      {!centerGroup ? (
        <>
          {permissions.includes('ASSIGN_CENTER_GROUP') ? (
            <Typography.Link
              variant="label-semibold"
              onClick={(e) => {
                e.stopPropagation()
                setOpen(true)
              }}
            >
              + Assigner à un groupe
            </Typography.Link>
          ) : (
            !hideUnassignLabel && (
              <Typography variant="label-semibold" className="text-red-secondary">
                Non assigné
              </Typography>
            )
          )}
        </>
      ) : (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <CenterGroupCircle size="medium" color={centerGroup.color} />
            <Typography variant="secondary-body">{centerGroup.name}</Typography>
          </div>
          {permissions.includes('UNASSIGN_CENTER_GROUP') && (
            <div
              className="flex justify-center items-center p-1 rounded-sm bg-secondaryRed-light"
              onClick={(e) => {
                e.stopPropagation()
                if (isUnassignEnrollmentPending) return
                unassignEnrollmentFromCenterGroupMutation({
                  centerId: enrollmentCenter.center.id,
                  groupId: centerGroup.id,
                  enrollmentCenterId: enrollmentCenter.id
                })
              }}
            >
              <UserCanceledIcon
                width="24px"
                height="24px"
                className="cursor-pointer"
                color={tailwindColors.secondaryRed.DEFAULT}
              />
            </div>
          )}
        </div>
      )}
      <Modal
        icon={PlusIcon}
        variant={'regular'}
        isOpen={open}
        onCancel={() => setOpen(false)}
        title={'Assigner à un sous-groupe'}
        hideActions
      >
        <AssignCenterGroupForm
          className={MODAL_BODY_CLASSNAME}
          centerId={enrollmentCenter.center.id}
          buttonsConfig={modalButtonConfig({
            onCancel: () => setOpen(false),
            isLoading: isAssignEnrollmentsPending,
            onConfirm: async (values) => {
              await assignEnrollmentsMutation({
                centerId: enrollmentCenter.center.id,
                groupId: values.groupId,
                ids: [enrollmentCenter.id]
              })
            }
          })}
        />
      </Modal>
    </div>
  )
}

export default AssignCenterGroupCell
